import { useCallback, useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";

import { styled } from "@mui/material/styles";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { WIPMessage } from "components/WIPMessage/WIPMessage";
import { AUTHORIZE_API, REDIRECT_URL } from "constants/auth.constants";
import { useNavigate } from "hooks/useNavigate";
import { ITenant } from "interfaces/auth.interface";
import {
  enhanceAuthorizeUrl,
  generateCodeChallenge,
  generateState,
  getAuthBaseUrl,
} from "utils/auth.util";

interface ILogin {
  client_id: string;
}

const LoginWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(2),
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  minHeight: "100vh",
}));

// grid-template-columns: repeat( 12, minmax(250px, 1fr) );
const TenantsGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: `repeat(auto-fit,minmax(200px,1fr))`,
  gap: theme.spacing(2),
  padding: theme.spacing(2),
}));

const TenantBox = styled(Button)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: theme.spacing(10),
}));

export function TenantScreen() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [userTenants, setUserTenants] = useState<ITenant[]>();

  // set local storage
  useEffect(() => {
    if (location.state?.email) {
      localStorage.setItem("LAST_USED_EMAIL", location.state.email);
    }
  }, [location.state?.email]);

  const { control } = useForm<ILogin>({
    defaultValues: {
      client_id: "",
    },
    reValidateMode: "onChange",
    mode: "onChange",
  });

  const onClickTenant = useCallback(
    async (clientId: string) => {
      const generatedState = generateState();
      const codeVerifier = generateState();
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      sessionStorage.setItem("state", generatedState);
      sessionStorage.setItem("code_verifier", codeVerifier);
      sessionStorage.setItem("client_id", clientId);

      localStorage.setItem("CLIENT_ID", clientId);

      const url = enhanceAuthorizeUrl({
        clientId: clientId,
        state: generatedState,
        codeChallenge: codeChallenge,
        authorizeUrl: getAuthBaseUrl() + AUTHORIZE_API,
        redirectUri: REDIRECT_URL,
        loginHint: location.state?.email ?? "",
      });

      globalThis.location.assign(url);
    },
    [location.state?.email],
  );

  useEffect(() => {
    const tenants = location.state?.tenants;

    if (!tenants || tenants.length === 0) {
      navigate("/auth/login");
    } else if (tenants.length === 1) {
      onClickTenant(tenants[0].client_id);
    } else {
      setUserTenants(tenants);
    }
  }, [location.state?.tenants, navigate, onClickTenant]);

  if (!userTenants) {
    return <div />;
  }

  return (
    <LoginWrapper>
      <WIPMessage messageKey={"tenants"} />
      <Box sx={{ width: "100%" }}>
        <Typography
          gutterBottom
          variant={"headingLargeRegular"}
          sx={{ textAlign: "center" }}
        >
          {t("select_a_tenant")}
        </Typography>
        <Typography gutterBottom sx={{ textAlign: "center" }}>
          {t("choose_a_workspace")}
        </Typography>
        <Controller
          name="client_id"
          control={control}
          render={() => (
            <TenantsGrid>
              {userTenants.map((tenant) => (
                <TenantBox
                  variant={"contained"}
                  color={"info"}
                  key={tenant.tenant_id}
                  onClick={() => onClickTenant(tenant.client_id)}
                >
                  {tenant.tenant_name}
                </TenantBox>
              ))}
            </TenantsGrid>
          )}
        />
      </Box>
    </LoginWrapper>
  );
}
